import type {
    ToolId,
    WorkspaceConfig,
    WorkspaceId,
} from './workspace-config.ts';

export type WorkspaceToolOrderOverrides = Partial<Record<WorkspaceId, ToolId[]>>;

interface LabelledItem {
    id: string;
    label: string;
}

export function sortToolsByLabel<T extends LabelledItem>(tools: readonly T[]): T[] {
    return [...tools].toSorted((a, b) => {
        const labelComparison = a.label.localeCompare(b.label, undefined, {
            numeric: true,
            sensitivity: 'base',
        });

        if (labelComparison !== 0) {
            return labelComparison;
        }

        return a.id.localeCompare(b.id);
    });
}

export function resolveWorkspacePrimaryToolIds(
    workspace: WorkspaceConfig,
    overrides: WorkspaceToolOrderOverrides,
): ToolId[] {
    if (workspace.primaryTools.length === 0) {
        return [];
    }

    const defaultIds = workspace.primaryTools;
    const defaultIdSet = new Set<ToolId>(defaultIds);
    const overrideIds = overrides[workspace.id] ?? [];
    const normalizedOverrideIds: ToolId[] = [];
    const normalizedOverrideSet = new Set<ToolId>();

    for (const id of overrideIds) {
        if (!defaultIdSet.has(id) || normalizedOverrideSet.has(id)) {
            continue;
        }

        normalizedOverrideIds.push(id);
        normalizedOverrideSet.add(id);
    }

    const missingDefaultIds = defaultIds.filter(id => !normalizedOverrideSet.has(id));

    return [...normalizedOverrideIds, ...missingDefaultIds];
}

export function reorderToolIds(order: readonly ToolId[], activeId: ToolId, overId: ToolId): ToolId[] {
    if (activeId === overId) {
        return [...order];
    }

    const fromIndex = order.indexOf(activeId);
    const toIndex = order.indexOf(overId);

    if (fromIndex === -1 || toIndex === -1) {
        return [...order];
    }

    const nextOrder = [...order];
    const [moved] = nextOrder.splice(fromIndex, 1);

    if (!moved) {
        return [...order];
    }

    nextOrder.splice(toIndex, 0, moved);

    return nextOrder;
}

export function applyWorkspaceToolReorder(
    workspace: WorkspaceConfig,
    overrides: WorkspaceToolOrderOverrides,
    activeId: ToolId,
    overId: ToolId,
): WorkspaceToolOrderOverrides {
    const currentOrder = resolveWorkspacePrimaryToolIds(workspace, overrides);

    if (currentOrder.length === 0) {
        return { ...overrides };
    }

    const nextOrder = reorderToolIds(currentOrder, activeId, overId);

    if (nextOrder.join('\u0000') === currentOrder.join('\u0000')) {
        return { ...overrides };
    }

    return {
        ...overrides,
        [workspace.id]: nextOrder,
    };
}
